import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomInput, CustomTextarea, CustomDatePicker } from "@components/Forms";
import DocumentTable from "@components/Tables/DocumentTable";
import { Breadcrumb, CustomSelect } from "@components/UI";
import ConfirmDeleteModal from "@components/UI/ConfirmDeleteModal";
import FullScreenDotLoader from "@components/UI/FullScreenDotLoader";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useFileGroupAPI } from "@hooks/useFileGroupAPI";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { formatDate, formatDateTime, normalizeResponseData, uppercase, translateDepartment } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { formatUserNameWithId } from "@utils/userDisplay";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";
import { CHARACTER_LENGTH } from "@constants/index";

const TEST_ID = "announcement-detail";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const fieldContainerTestId = (name) => tid(`field-${name}-container`);
const fieldLabelTestId = (name) => tid(`field-${name}-label`);
const fieldInputTestId = (name) => tid(`field-${name}-input`);
const fieldValueTestId = (name) => tid(`field-${name}-value`);

export default function AnnouncementDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { bulletinSeq } = useParams();
  const [isEditMode, setIsEditMode] = useState(false);
  const [announcementData, setAnnouncementData] = useState({});
  const [editingData, setEditingData] = useState({});
  const [isUnchanged, setIsUnchanged] = useState(true);
  const [hasErrors, setHasErrors] = useState(false);
  const [fileInfoModified, setFileInfoModified] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [documentCurrentPage, setDocumentCurrentPage] = useState(1);
  const [documentItemsPerPage] = useState(5);
  const { postClassList, postTypeList, fetchSearchInfo } = useSearchInfo();
  const { fileInfo, documentTotalData, getFileInformationPagination, uploadFilesAndGetOnSuccess } = useFileGroupAPI(
    announcementData?.fileGroupNo || 0,
    documentCurrentPage || 1,
  );

  // API mutations
  const getAnnouncementData = useMutationData(
    `${queryKeys.getAnnouncementById}/${bulletinSeq}`,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          const normalizedData = normalizeResponseData(response);
          setAnnouncementData(normalizedData);
          setEditingData(normalizedData);
        }
      },
      onError: (error) => {
        if (error?.response?.status === 404) {
          navigate("/404", { state: { backTo: "/announcements" } });
        } else {
          showErrorToast(t("bulletin.get-announcement-error-title"));
        }
        console.error("Error getting announcement data:", error);

      },
    },
  );

  useEffect(() => {
    getAnnouncementData.mutateAsync();
  }, []);

  const increaseViewCount = useMutationData(`${queryKeys.increaseViewCountAnnouncement}/${bulletinSeq}`, {
    method: "PUT",
  });

  const updateAnnouncement = useMutationData(
    `${queryKeys.updateAnnouncement}/${bulletinSeq}`,
    { method: "PUT" },
    {
      onSuccess: async (response) => {
        if (typeof response !== "string") {
          await updateDataInIndexedDb(STORES.BULLETINS, response, response.bulletinSeq);
          showSuccessToast(t("bulletin.announcement-update-success"));

          // Handle file upload if files are selected
          if (newFiles?.length > 0) {
            const uploadFilesData = newFiles.reduce((acc, file, index) => {
              if (file instanceof File) {
                acc[`file_${index}`] = file;
              }
              return acc;
            }, {});

            const formData = new FormData();
            formData.append("targetSeq", bulletinSeq);
            formData.append("targetTypeCd", "BULLETIN");
            formData.append("fileGroupNo", announcementData.fileGroupNo);

            Object.entries(uploadFilesData).forEach(([key, file]) => {
              formData.append(key, file, file.name);
            });

            uploadFilesAndGetOnSuccess.mutate(formData);
          } else if (fileInfoSeqListToDelete?.length > 0) {
            getFileInformationPagination.mutate();
          }

          getAnnouncementData.mutate();
          setIsEditMode(false);
          setIsUnchanged(true);
        }
      },
      onError: (error) => {
        console.error("Error updating announcement:", error);
        showErrorToast(t("bulletin.announcement-update-success"));
      },
    },
  );

  // API call for deleting files
  const deleteFile = useMutationData(
    (fileInfoSeq) => `file_info/${fileInfoSeq}`,
    {
      method: "DELETE",
    },
    {
      onSuccess: () => {
        showSuccessToast(t("project.document.delete-success"));
        // Refresh file list after deletion
        if (announcementData.fileGroupNo && announcementData.fileGroupNo !== 0) {
          getFileInformationPagination.mutate();
          // Calculate new maximum number of pages
          const maxPages = Math.ceil((documentTotalData - 1) / documentItemsPerPage);
          // If current page exceeds maxPages, switch to the last valid page
          if (documentCurrentPage > maxPages && maxPages > 0) {
            setDocumentCurrentPage(maxPages);
          } else if (documentTotalData <= 1) {
            // If no files remain, reset to page 1
            setDocumentCurrentPage(1);
          }
        }
      },
      onError: (error) => {
        console.error("Error during delete file:", error);
        showErrorToast(t("project.document.delete-error"));
      },
    },
  );

  // Effects
  useEffect(() => {
    if (bulletinSeq) {
      fetchSearchInfo();
      increaseViewCount.mutateAsync();
    }
  }, [bulletinSeq]);

  useEffect(() => {
    if (announcementData.fileGroupNo && announcementData.fileGroupNo !== 0) {
      getFileInformationPagination.mutate();
    }
  }, [announcementData.fileGroupNo, documentCurrentPage]);

  useEffect(() => {
    // Calculate the maximum number of pages based on documentTotalData
    const maxPages = Math.ceil((documentTotalData || 0) / documentItemsPerPage);

    // If documentCurrentPage exceeds maxPages, switch to the last valid page
    if (documentCurrentPage > maxPages && maxPages > 0) {
      setDocumentCurrentPage(maxPages);
    } else if (documentTotalData === 0) {
      // If there are no files left, reset to page 1
      setDocumentCurrentPage(1);
    }
  }, [documentTotalData, documentItemsPerPage, documentCurrentPage]);

  useEffect(() => {
    setFileInfoModified(fileInfo);
  }, [fileInfo]);

  // File management logic
  const validFiles = fileInfoModified.filter((file) => file.fileInfoSeq || file.file);
  const hasBoth = validFiles.filter((file) => file.fileInfoSeq && file.file);
  const newFiles = validFiles.map((file) => file.file).filter(Boolean);
  const deletedFiles = fileInfo.filter((orig) => !validFiles.some((file) => file.fileInfoSeq === orig.fileInfoSeq));
  const fileInfoSeqListToDelete = [
    ...hasBoth.map((file) => file.fileInfoSeq),
    ...deletedFiles.map((file) => file.fileInfoSeq),
  ];

  useEffect(() => {
    setIsUnchanged(hasBoth.length === 0 && newFiles.length === 0 && deletedFiles.length === 0 && isUnchanged);
  }, [fileInfoModified]);

  // Validation
  useEffect(() => {
    const hasRequiredFields = editingData.title?.trim() && editingData.content?.trim();

    // Compare specific fields instead of using JSON.stringify
    const dataChanged =
      editingData.title !== announcementData.title ||
      editingData.content !== announcementData.content ||
      editingData.postClassCd !== announcementData.postClassCd ||
      editingData.postTypeCd !== announcementData.postTypeCd ||
      editingData.mainPopupFlag !== announcementData.mainPopupFlag ||
      editingData.postStartDt !== announcementData.postStartDt ||
      editingData.postEndDt !== announcementData.postEndDt;

    setHasErrors(!hasRequiredFields);
    setIsUnchanged(!dataChanged);
  }, [editingData, announcementData]);

  // Check if save button should be enabled
  const canSave = editingData.title?.trim() && editingData.content?.trim() && !isUnchanged;

  // Handlers
  const handleEdit = () => {
    setIsEditMode(true);
    setEditingData({ ...announcementData });
    // Initialize date range for DatePicker
    const startDate = announcementData.postStartDt ? new Date(announcementData.postStartDt) : null;
    const endDate = announcementData.postEndDt ? new Date(announcementData.postEndDt) : null;
    setDateRange([startDate, endDate]);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditingData({ ...announcementData });
    setFileInfoModified(fileInfo);
    // Reset date range
    const startDate = announcementData.postStartDt ? new Date(announcementData.postStartDt) : null;
    const endDate = announcementData.postEndDt ? new Date(announcementData.postEndDt) : null;
    setDateRange([startDate, endDate]);
  };

  const handleSave = () => {
    if (!hasErrors && !isUnchanged) {
      updateAnnouncement.mutate({
        ...announcementData,
        ...editingData,
      });
    }
  };

  const handleInputChange = (field, value) => {
    setEditingData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // File management functions
  const handleUploadFile = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.multiple = true;
    fileInput.accept = "*/*";
    fileInput.onchange = (event) => {
      const files = Array.from(event.target.files);
      if (files.length > 0) {
        if (!bulletinSeq) {
          showErrorToast("Missing bulletin sequence.");
          return;
        }

        const uploadFilesData = files.reduce((acc, file, index) => {
          if (file instanceof File) {
            acc[`file_${index}`] = file;
          }
          return acc;
        }, {});

        const formData = new FormData();
        formData.append("targetSeq", bulletinSeq);
        formData.append("targetTypeCd", "BULLETIN");
        formData.append("fileGroupNo", announcementData.fileGroupNo);

        Object.entries(uploadFilesData).forEach(([key, file]) => {
          formData.append(key, file, file.name);
        });

        uploadFilesAndGetOnSuccess.mutate(formData, {
          onSuccess: () => {
            // Refresh announcementData to update fileGroupNo
            getAnnouncementData.mutateAsync().then(() => {
              // Refresh file list with the current page
              getFileInformationPagination.mutate();
              // Calculate new number of pages based on updated documentTotalData
              const maxPages = Math.ceil((documentTotalData + files.length) / documentItemsPerPage);
              // Only update documentCurrentPage if the current page is invalid
              if (documentCurrentPage > maxPages && maxPages > 0) {
                setDocumentCurrentPage(maxPages);
              } else if (documentTotalData === 0 && files.length > 0) {
                setDocumentCurrentPage(1); // Reset to page 1 if there were no files before
              }
              // Otherwise, do not change documentCurrentPage
            });
          },
          onError: (error) => {
            console.error("Error during upload:", error);
            showErrorToast(t("common.files-upload-error"));
          },
        });
      } else {
        showErrorToast("No files selected");
      }
    };

    fileInput.click();
  };

  // Document delete modal state
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);

  const handleDeleteDocument = (documentToDelete) => {
    setDocumentToDelete(documentToDelete);
    setShowDeleteDocumentModal(true);
  };

  const handleConfirmDeleteDocument = () => {
    if (!documentToDelete) return;
    setDeleteDocumentLoading(true);

    deleteFile.mutate(documentToDelete.fileInfoSeq, {
      onSettled: () => {
        setDeleteDocumentLoading(false);
        setShowDeleteDocumentModal(false);
        setDocumentToDelete(null);
      },
    });
  };

  if (getAnnouncementData.isPending) {
    return (
      <div data-testid={tid("loading")}>
        <LoadingSpinner data-testid={tid("loading-spinner")} />
      </div>
    );
  }

  return (
    <div className={`${colors.bgSecondary}`} data-testid={tid("page")}>
      {/* Header */}
      <div className="mb-4" data-testid={tid("header")}>
        {/* Breadcrumb */}
        <Breadcrumb
          data-testid={tid("breadcrumb")}
          items={[
            { label: t("navbar.announcement-list"), href: "/announcements" },
            {
              label: t("navbar.announcement-details"),
              href: null,
              onClick: isEditMode ? () => setIsEditMode(false) : undefined,
            },
            ...(isEditMode ? [{ label: t("bulletin.edit-announcement") }] : []),
          ]}
          className="mb-4"
        />

        {/* Title and Action Buttons */}
        <div className="flex items-center justify-between" data-testid={tid("title-row")}>
          <h1
            className={`text-2xl font-bold ${colors.textPrimary} truncate`}
            data-testid={tid("page-title")}
          >
            {announcementData?.title || "LOADING..."}
          </h1>
        </div>
      </div>

      {/* Main Information Cards */}
      <div
        className={`rounded-2xl border ${colors.borderPrimary} ${colors.bgPrimary}`}
        data-testid={tid("information-card")}
      >
        <div className="flex flex-col lg:flex-row">
          {/* Announcement Information Card */}
          <div className={`w-full p-6`}>
            <div className="flex items-center justify-between mb-2">
              <h2
                className={`text-lg font-bold ${colors.textPrimary}`}
                data-testid={tid("information-title")}
              >
                {uppercase(t("bulletin.announcement-information"))}
              </h2>
              {isEditMode ? (
                <div className="flex items-center justify-end space-x-3" data-testid={tid("edit-actions")}>
                  <OutlineButton data-testid={tid("cancel-button")} onClick={handleCancel}>
                    {t("common.cancel-button")}
                  </OutlineButton>
                  <PrimaryButton data-testid={tid("save-button")} onClick={handleSave} disabled={!canSave}>
                    {t("common.save-button")}
                  </PrimaryButton>
                </div>
              ) : (
                <OutlineButton data-testid={tid("edit-button")} onClick={handleEdit}>
                  {t("bulletin.edit-announcement")}
                </OutlineButton>
              )}
            </div>

            {!isEditMode ? (
              <div className={`pt-4`} data-testid={tid("content-view")}>
                <div data-testid={fieldContainerTestId("content")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("content")}>
                    {t("bulletin.content")}
                  </label>
                  <CustomTextarea
                    data-testid={fieldValueTestId("content")}
                    rows={6}
                    value={announcementData?.content || ""}
                    title={announcementData?.content}
                    readOnly={true}
                  />
                </div>
              </div>
            ) : (
              <div className={`pt-4`} data-testid={tid("form")}>
                <div data-testid={fieldContainerTestId("title")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("title")}>
                    {t("bulletin.title")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomInput
                    data-testid={fieldInputTestId("title")}
                    type="text"
                    placeholder={t("bulletin.enter-title")}
                    value={editingData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    title={editingData.title}
                    maxLength={CHARACTER_LENGTH.COMMENT}
                  />
                </div>
                <div className="md:col-span-2 mt-6" data-testid={fieldContainerTestId("content")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("content")}>
                    {t("bulletin.content")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomTextarea
                    data-testid={fieldInputTestId("content")}
                    rows={6}
                    placeholder={t("bulletin.enter-content")}
                    value={editingData.content}
                    onChange={(e) => handleInputChange("content", e.target.value)}
                    title={editingData.content}
                    // size="sm"
                    maxLength={CHARACTER_LENGTH.CONTENT}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="announcement-detail-content-char-count">{(editingData.content || "").length}/{CHARACTER_LENGTH.CONTENT}</p>
                </div>
              </div>
            )}
            <div className={`space-y-0`} data-testid={tid("metadata-section")}>
              {/* Row 1: 5 fields in one line */}
              <div className={`grid grid-cols-5 gap-8 py-4 border-b border-dashed ${colors.borderPrimary}`} data-testid={tid("metadata-row-1")}>
                <div data-testid={fieldContainerTestId("post-class")}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("post-class")}>
                    {t("bulletin.post-class")}
                  </span>
                  {isEditMode ? (
                    <CustomSelect
                      data-testid={fieldInputTestId("post-class")}
                      options={
                        postClassList?.map((option) => ({
                          value: option.value,
                          label: t(option.label),
                        })) || []
                      }
                      value={
                        editingData.postClassCd
                          ? {
                            value: editingData.postClassCd,
                            label: t(postClassList?.find((o) => o.value === editingData.postClassCd)?.label || ""),
                          }
                          : null
                      }
                      onChange={(opt) => handleInputChange("postClassCd", opt?.value || "")}
                      placeholder={t("bulletin.post-class-options-placeholder")}
                      size="md"
                      menuPlacement="auto"
                      clearable={true}
                    />
                  ) : (
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid={fieldValueTestId("post-class")}
                    >
                      {t(postClassList?.find((opt) => opt.value === announcementData?.postClassCd)?.label) || "---"}
                    </span>
                  )}
                </div>

                <div data-testid={fieldContainerTestId("post-date-range")}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("post-date-range")}>
                    {t("bulletin.post-date-range")}
                  </span>
                  {isEditMode ? (
                    <CustomDatePicker
                      data-testid={fieldInputTestId("post-date-range")}
                      rangeSeparator=' → '
                      selected={dateRange[0]}
                      onChange={(dates) => {
                        const [start, end] = dates;
                        setDateRange([start, end]);
                        // Format dates to YYYY-MM-DD for form submission
                        const startDateStr = start ? start.toLocaleDateString("en-CA") : "";
                        const endDateStr = end ? end.toLocaleDateString("en-CA") : "";

                        setEditingData((prev) => ({
                          ...prev,
                          postStartDt: startDateStr,
                          postEndDt: endDateStr,
                        }));
                      }}
                      startDate={dateRange[0]}
                      endDate={dateRange[1]}
                      selectsRange
                      placeholderText={t("bulletin.select-date-range")}
                      className={`h-10 w-full appearance-none rounded-lg border p-2 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} transition-colors duration-300`}
                      dateFormat="yyyy-MM-dd"
                      minDate={new Date()}
                      filterDate={(date) => date >= new Date(new Date().setHours(0, 0, 0, 0))}
                    />
                  ) : (
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid={fieldValueTestId("post-date-range")}
                    >
                      {announcementData?.postStartDt && announcementData?.postEndDt
                        ? `${formatDate(announcementData?.postStartDt)} → ${formatDate(announcementData?.postEndDt)}`
                        : "---"}
                    </span>
                  )}
                </div>

                <div data-testid={fieldContainerTestId("main-popup")}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-3`} data-testid={fieldLabelTestId("main-popup")}>
                    {t("bulletin.main-popup")}
                  </span>
                  {isEditMode ? (
                    <label className="relative inline-flex cursor-pointer hover:cursor-pointer items-center justify-start">
                      <input
                        data-testid={fieldInputTestId("main-popup")}
                        type="checkbox"
                        className="sr-only peer"
                        checked={editingData.mainPopupFlag === "Y"}
                        onChange={(e) => handleInputChange("mainPopupFlag", e.target.checked ? "Y" : "N")}
                      />
                      <div
                        className={`w-[3rem] h-[1.40625rem] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[1.59375rem] ${colors.controlThumb} after:content-[''] after:absolute after:top-[0.15625rem] after:left-[0.15625rem] after:rounded-full after:h-[1.09375rem] after:w-[1.09375rem] after:transition-all after:shadow-sm border ${editingData.mainPopupFlag !== "Y" ? colors.borderPrimary : "border-none"} ${editingData.mainPopupFlag === "Y" ? colors.bgActiveToggle : colors.bgInput}`}
                      ></div>
                      <span className={`ml-2 text-sm font-medium ${colors.textNormal}`}>
                        {editingData.mainPopupFlag === "Y" ? t("common.yes") : t("common.no")}
                      </span>
                    </label>
                  ) : (
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid={fieldValueTestId("main-popup")}
                    >
                      {announcementData?.mainPopupFlag === "Y" ? t("common.yes") : t("common.no")}
                    </span>
                  )}
                </div>

                {!isEditMode && (
                <div data-testid={fieldContainerTestId("view-count")}>
                      <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("view-count")}>
                        {t("bulletin.view-count")}
                      </span>
                      <span
                        className={`text-sm font-medium ${colors.textMessage}`}
                        data-testid={fieldValueTestId("view-count")}
                      >
                        {announcementData?.viewCnt || "0"}
                      </span>
                </div>
                )}

                {/* <div data-testid={fieldContainerTestId("creator-department")}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("creator-department")}>
                    {t("bulletin.creator-department")}
                  </span>
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("creator-department")}
                      type="text"
                      disabled={true}
                      value={translateDepartment(t, announcementData?.createrDeptCdName) || ""}
                    />
                  ) : (
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid={fieldValueTestId("creator-department")}
                    >
                      {translateDepartment(t, announcementData?.createrDeptCdName)|| "---"}
                    </span>
                  )}
                </div> */}
              </div>

              {/* Row 2: 5 fields in one line */}
              <div className={`grid grid-cols-5 gap-8 pt-4`} data-testid={tid("metadata-row-2")}>
                <div className="min-w-0" data-testid={fieldContainerTestId("creator")}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("creator")}>
                    {t("common.creator")}
                  </span>
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("creator")}
                      type="text"
                      disabled={true}
                      value={formatUserNameWithId(announcementData?.createrName, announcementData?.createrId)}
                    />
                  ) : (
                    <UserNameWithId
                      name={announcementData?.createrName}
                      id={announcementData?.createrId}
                      layout="inline"
                      data-testid={fieldValueTestId("creator")}
                    />
                  )}
                </div>

                <div data-testid={fieldContainerTestId("creation-date")}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("creation-date")}>
                    {t("bulletin.creation-date")}
                  </span>
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("creation-date")}
                      type="text"
                      disabled={true}
                      value={formatDateTime(announcementData?.createdAt) || ""}
                    />
                  ) : (
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid={fieldValueTestId("creation-date")}
                    >
                      {formatDateTime(announcementData?.createdAt) || "---"}
                    </span>
                  )}
                </div>

                <div className="min-w-0" data-testid={fieldContainerTestId("last-editor")}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("last-editor")}>
                    {t("common.last-modified-by")}
                  </span>
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("last-editor")}
                      type="text"
                      disabled={true}
                      value={formatUserNameWithId(announcementData?.updaterName, announcementData?.updaterId)}
                    />
                  ) : (
                    <UserNameWithId
                      name={announcementData?.updaterName}
                      id={announcementData?.updaterId}
                      layout="inline"
                      data-testid={fieldValueTestId("last-editor")}
                    />
                  )}
                </div>

                <div data-testid={fieldContainerTestId("last-modified")}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("last-modified")}>
                    {t("common.last-modified-date")}
                  </span>
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("last-modified")}
                      type="text"
                      disabled={true}
                      value={formatDateTime(announcementData?.updatedAt) || ""}
                    />
                  ) : (
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid={fieldValueTestId("last-modified")}
                    >
                      {formatDateTime(announcementData?.updatedAt) || "---"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* File Information Section */}
      <div
        className={`rounded-3xl border ${colors.borderPrimary} ${colors.bgPrimary} px-6 pt-6 pb-2 mt-6`}
        data-testid={tid("attachments-section")}
      >
        <div className="flex items-center justify-between mb-2" data-testid={tid("attachments-header")}>
          <h2
            className={`font-medium ${colors.textPrimary}`}
            data-testid={tid("attachments-title")}
          >
            {uppercase(t("bulletin.attachment"))} ({documentTotalData  || 0})
          </h2>
          <OutlineButton data-testid={tid("upload-file-button")} onClick={handleUploadFile}>
            {t("project.document.upload-file")}
          </OutlineButton>
        </div>

        {fileInfoModified && fileInfoModified.length > 0 ? (
          <div data-testid={tid("documents-table")}>
          <DocumentTable
            data={fileInfoModified}
            onDelete={handleDeleteDocument}
            currentPage={documentCurrentPage}
            totalPages={Math.ceil((documentTotalData || 0) / documentItemsPerPage)}
            onPageChange={setDocumentCurrentPage}
            totalData={documentTotalData}
          />
          </div>
        ) : (
          <div
            className={`py-8 text-center ${colors.textSecondary}`}
            data-testid={tid("no-documents")}
          >
            <p data-testid={tid("no-documents-text")}>{t("project.no-documents-found")}</p>
          </div>
        )}
      </div>

      {/* Confirm Delete Document Modal */}
      <ConfirmDeleteModal
        data-testid={tid("delete-document-modal")}
        isOpen={showDeleteDocumentModal}
        onClose={() => setShowDeleteDocumentModal(false)}
        onConfirm={handleConfirmDeleteDocument}
        title={t("bulletin.delete-document")}
        description={t("bulletin.delete-document-description")}
        loading={deleteDocumentLoading}
      />
      <FullScreenDotLoader
        data-testid={tid("upload-loader")}
        loading={uploadFilesAndGetOnSuccess.isPending}
        text={
          uploadFilesAndGetOnSuccess.isPending
            ? t("bulletin.uploading-announcement") || "Uploading announcement file..."
            : t("bulletin.upload-announcement-file") || "Uploading announcement file..."
        }
        size={80}
        speedMultiplier={1.2}
      />
    </div>
  );
}
